/**
 * components/ChatSuggestions.js — Phase 4 Week 2
 *
 * Quick-prompt chips shown when the conversation is empty.
 * Filtered by the user's roles — each role sees relevant suggestions only.
 * Disappears after the first message is sent.
 */
import React from 'react';
import { Box, Chip, Typography } from '@mui/material';
import { AutoAwesome } from '@mui/icons-material';

// ── Suggestions per role ──────────────────────────────────────────────────────
const SUGGESTIONS = {
  ROLE_SUPER_USER: [
    'Overall HR health summary',
    'What is our current headcount?',
    'Budget vs actuals this month',
    'Which department has highest attrition?',
    'Top skill gaps to address',
  ],
  ROLE_WORKFORCE_PULSE: [
    'Headcount trend this year',
    'Which department grew fastest?',
    'Gender diversity breakdown',
    'Attrition forecast next quarter',
  ],
  ROLE_FINANCE_BUDGET: [
    'Are we on track with payroll budget?',
    'Top 3 expense claim categories',
    'Month-on-month payroll change',
    'Overtime cost trend',
  ],
  ROLE_OPERATIONS: [
    "Today's attendance rate",
    'Most common leave type this month',
    'Which department has the highest leave liability?',
    'Lateness hotspots this week',
  ],
  ROLE_TALENT_PERFORMANCE: [
    'Performance bell curve summary',
    'Top skill gaps to address',
    'Most requested training courses',
    'How many employees exceed expectations?',
  ],
};

const FALLBACK = [
  'What HR data can you show me?',
  'What questions can I ask?',
];

/**
 * @param {string[]} userRoles   — from AuthContext user.roles
 * @param {function} onSelect    — called with the suggestion string
 */
export default function ChatSuggestions({ userRoles = [], onSelect }) {
  // Union across all roles; preserve order, deduplicate
  const seen = new Set();
  const chips = [];
  for (const role of userRoles) {
    for (const s of SUGGESTIONS[role] || []) {
      if (!seen.has(s)) { seen.add(s); chips.push(s); }
    }
  }
  const suggestions = chips.length ? chips.slice(0, 5) : FALLBACK;

  return (
    <Box sx={{ px: 2, py: 1.5 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
        <AutoAwesome sx={{ fontSize: 14, color: 'primary.main' }} />
        <Typography variant="caption" fontWeight={700} color="text.secondary"
          sx={{ textTransform: 'uppercase', letterSpacing: '0.6px', fontSize: '0.68rem' }}>
          Suggested questions
        </Typography>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.75 }}>
        {suggestions.map((s) => (
          <Chip
            key={s}
            label={s}
            size="small"
            onClick={() => onSelect(s)}
            variant="outlined"
            sx={{
              fontSize: '0.85rem',
              fontWeight: 500,
              cursor: 'pointer',
              borderColor: 'primary.light',
              color: 'primary.main',
              bgcolor: 'transparent',
              '&:hover': {
                bgcolor: 'primary.main',
                color:   '#fff',
                borderColor: 'primary.main',
              },
              transition: 'all 180ms ease',
            }}
          />
        ))}
      </Box>
    </Box>
  );
}
